import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';

const HANDSHAKE_TIMEOUT_MS = 5000;

type HandshakeStatus = 'idle' | 'pending' | 'done' | 'error';

interface EmbedMessage {
  type: string;
  payload: object;
  sessionToken?: string;
}

@Injectable({ providedIn: 'root' })
export class IframeSecurityService implements OnDestroy {
  private handshakeStatus: HandshakeStatus = 'idle';
  private handshakeTimer: ReturnType<typeof setTimeout> | null = null;
  private sessionToken: string | null = null;

  constructor(private router: Router) {
    this.listenForHostMessages();
    this.initiateHandshake();
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.onMessage);
    this.clearHandshakeTimer();
  }

  isInsideAllowedIframe(): boolean {
    const standalone = window.self === window.top;
    const origin = this.getHostOrigin();

    if (standalone || !origin) {
      return false;
    }

    return Object.keys(environment.allowedRoutesByOrigin).includes(origin);
  }

  isRouteAllowedForOrigin(route: string): boolean {
    const origin = this.getHostOrigin();
    if (!origin) {
      return false;
    }
    const allowedRoutes = environment.allowedRoutesByOrigin[origin] || [];
    return allowedRoutes.some((allowedRoute: string) => route.includes(allowedRoute));
  }

  isHandshakeDone(): boolean {
    return this.handshakeStatus === 'done';
  }

  send(type: string, payload: object): void {
    const hostOrigin = this.getHostOrigin();

    if (!this.isInsideAllowedIframe() || !this.isHandshakeDone() || !hostOrigin) {
      return;
    }

    const message: EmbedMessage = { type, payload, sessionToken: this.sessionToken };
    window.parent.postMessage(message, hostOrigin);
  }

  sendActionCompleted(payload: object): void {
    this.send('ACTION_COMPLETED', payload);
  }

  sendCustomMessage(payload: object): void {
    this.send('CUSTOM_MESSAGE', payload);
  }

  private getHostOrigin(): string | null {
    const ancestors = window.location.ancestorOrigins;

    if (ancestors?.length > 0) {
      return ancestors[0];
    }

    if (document.referrer) {
      try {
        return new URL(document.referrer).origin;
      } catch {
        return null;
      }
    }

    return null;
  }

  private initiateHandshake(): void {
    const hostOrigin = this.getHostOrigin();

    if (!this.isInsideAllowedIframe() || !hostOrigin) {
      return;
    }

    this.handshakeStatus = 'pending';
    window.parent.postMessage({ type: 'INIT', payload: {} }, hostOrigin);

    this.handshakeTimer = setTimeout(() => {
      if (this.handshakeStatus !== 'done') {
        this.handshakeStatus = 'error';
        this.router.navigate(['/frame-blocked']);
      }
    }, HANDSHAKE_TIMEOUT_MS);
  }

  private listenForHostMessages = (): void => {
    if (window.self === window.top) {
      return;
    }

    window.addEventListener('message', this.onMessage);
  };

  private clearHandshakeTimer(): void {
    if (this.handshakeTimer !== null) {
      clearTimeout(this.handshakeTimer);
      this.handshakeTimer = null;
    }
  }

  private onMessage = (event: MessageEvent): void => {
    if (!Object.keys(environment.allowedRoutesByOrigin).includes(event.origin)) {
      return;
    }

    const data = event.data as EmbedMessage;
    const payload = data?.payload as Record<string, unknown>;
    const isActionCompleted = data?.type === 'ACTION_COMPLETED';
    const isInitCompleted = payload?.['action'] === 'INITIALIZED';

    if (isActionCompleted && isInitCompleted && data?.sessionToken) {
      this.clearHandshakeTimer();
      this.sessionToken = data.sessionToken as string;
      this.handshakeStatus = 'done';
    }
  };
}
